import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
// The two row shapes this board joins, imported rather than restated.
import type { AgentSatisfaction } from "@tdelivr/api-client/generated/support/support-case.js";
import type { WorkforceMember } from "@tdelivr/api-client/generated/identity/workforce-identity.js";

import { exportSheet, type ExportFormat } from "../export.ts";
import {
  agentCell,
  agentDetail,
  agentPage,
  agentRows,
  BOARD_NOTE,
  EXPORT_RANGE_REFUSAL,
  exactScore,
  fetchedNote,
  LISTING_NOTE,
  MEMBERS_SCOPE,
  PER_FETCH,
  SATISFACTION_SCOPE,
  scoreCell,
  stateOf,
  type AgentOrder,
  type AgentRow,
} from "../satisfaction.ts";
import type { Session } from "../session.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { GridPager, GridToolbar, NoMatch } from "./DataGrid.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * How customers rated the support they got, per agent.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `csat`.
 *
 * **This page's recorded blocker was right, and ADR 0088 is why it was re-measured anyway.** It said
 * nothing on the platform models support satisfaction and cited SUP-05 — the escalation service,
 * which holds policies and timers. That citation half is exactly right, and it is the same shape as
 * the replies board's note, which was right about SUP-04 and wrong about everything else because
 * nobody had asked whether a different service held the thing.
 *
 * So the question was asked first. **Nothing held it**: RAT-01 is the only rating service, its
 * `transactionType` is a one-member enum (`TRIP`) and it publishes no listing at all, so trip
 * ratings have no board either. ADR 0090 adds the record to SUP-01, the service that owns the case,
 * because the design's own precondition — "after a case closes" — is a row that already exists
 * there.
 *
 * What is drawn and what is not — `../satisfaction.ts` argues each:
 *
 * | drawn                      | source                                                         |
 * | -------------------------- | -------------------------------------------------------------- |
 * | the rows                   | SUP-01 `GET /v1/tenants/{tenantId}/support-satisfaction`      |
 * | `Agent` — `J. Wekesa`      | `agentSubject` → IAM-02 `workforce_members.displayName`       |
 * | `Cases` — `142`            | `ratedCases`, which is **rated** cases and not cases handled  |
 * | `CSAT` — `4.8`             | `scoreTotal / ratedCases`, divided here to one decimal        |
 * | `done · top`               | **not drawn** — a judgement against a threshold nothing holds |
 * | `caution · coaching`       | **not drawn** — the same absence                               |
 * | the four tiles             | **not drawn** — four different absences, two of them R-04     |
 * | `Export ratings`           | drawn; the format works and the date range is refused         |
 *
 * **Two operations, and the join is the point.** SUP-01 holds a Keycloak subject and no names;
 * IAM-02's `workforce_members` holds `displayName` keyed by `platformSubject`, which ADR 0084
 * published a listing of. So this is a two-hop read like `Multi-stop in trip`'s address hop — and an
 * agent with no workforce record is drawn as the subject with the reason, never as a name this
 * console invented.
 *
 * **The second hop may fail on its own**, and when it does the board still draws. A satisfaction
 * listing a console can read and a member listing it cannot is a board of subjects rather than no
 * board at all, and the subjects are the figures somebody is assessed by.
 */

const caseApi = services.support__support_case;
const workforceApi = services.identity__workforce_identity;

export interface SatisfactionProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: tenant, console, group, page. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly agents: readonly AgentSatisfaction[];
  readonly members: readonly WorkforceMember[];
  readonly cursor: string | undefined;
  /** Why no name could be shown, when the member listing was the half that failed. */
  readonly membersUnread: string | undefined;
}

export function Satisfaction({ transport, session, crumbs }: SatisfactionProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<AgentOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);

  const load = useCallback(async () => {
    const tenantId = session.tenantId;
    try {
      const answered = await caseApi
        .createSupportCaseClient(transport)
        .listAgentSatisfaction(tenantId, String(PER_FETCH), undefined);

      /*
       * The second hop, and it is allowed to fail on its own.
       *
       * A board of subjects is worse than a board of names and far better than no board: the
       * figures are what somebody is assessed by, and withholding them because a *name* could not
       * be read would be this screen deciding that a number is only worth showing when it is
       * flattering to look at. So the failure is recorded and said under the grid.
       */
      let members: readonly WorkforceMember[] = [];
      let membersUnread: string | undefined;
      if (session.scopes.has(MEMBERS_SCOPE)) {
        try {
          const people = await workforceApi
            .createWorkforceIdentityClient(transport)
            .listWorkforceMembers(tenantId, String(PER_FETCH), undefined);
          members = people.members;
        } catch {
          membersUnread =
            "IAM-02's member listing could not be read, so these rows show each agent's platform subject rather than their name. The figures are SUP-01's and are unaffected.";
        }
      } else {
        membersUnread = `This sign-in does not hold ${MEMBERS_SCOPE}, so these rows show each agent's platform subject rather than their name. The figures are SUP-01's and are unaffected.`;
      }

      setLoaded({
        agents: answered.agents,
        members,
        cursor: answered.nextAfter ?? undefined,
        ...(membersUnread === undefined ? { membersUnread: undefined } : { membersUnread }),
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, [SATISFACTION_SCOPE]));
      setLoaded(undefined);
    }
  }, [transport, session.tenantId, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The next page, from the cursor the service answered.
   *
   * The cursor is an agent subject — the grouping's own ordering and the only immutable one it has.
   * Every instant on these rows moves as ratings arrive, so paging over `latestRatingAt` would move
   * an agent who was just rated past the cursor or back behind it. ADR 0090 §3.
   */
  const loadMore = useCallback(async () => {
    const held = loaded;
    if (held?.cursor === undefined || fetching) return;
    setFetching(true);
    try {
      const answered = await caseApi
        .createSupportCaseClient(transport)
        .listAgentSatisfaction(session.tenantId, String(PER_FETCH), held.cursor);
      setLoaded({
        ...held,
        agents: [...held.agents, ...answered.agents],
        cursor: answered.nextAfter ?? undefined,
      });
      setPage((current) => current + 1);
    } catch (error) {
      setFailure(stateForError(error, [SATISFACTION_SCOPE]));
    } finally {
      setFetching(false);
    }
  }, [loaded, fetching, transport, session.tenantId]);

  const rows = useMemo(() => agentRows(loaded?.agents ?? [], loaded?.members ?? []), [loaded]);
  const selected = rows.find((row) => row.agentSubject === selectedSubject);

  const view = useMemo(
    () => agentPage(rows, { search, order, page, perPage }),
    [rows, search, order, page, perPage],
  );

  const viewState = failure ?? stateForList(loaded === undefined ? undefined : rows);

  const clearFilters = (): void => {
    setSearch("");
    setPage(1);
  };

  const onNext = (): void => {
    if (page < view.pageCount) setPage(page + 1);
    else void loadMore();
  };

  const note = fetchedNote(
    loaded === undefined ? undefined : rows.length,
    loaded?.cursor !== undefined,
  );

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Agent</span>
      <span>Cases</span>
      <span>CSAT</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      {/*
        The page and its detail panel in one positioned container, which is what docks the panel.

        Both docking rules are written against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes the panel a 420px second column, and below 1240 it is
        `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s own
        `position: relative`. This board had neither — the panel was rendered inside the page, and
        on seven of these twelve boards inside the closing-note callout, where it was a flex item
        beside an `info` glyph and a sentence.

        `Settlement.tsx` and `Invoices.tsx` were already this shape, which is why they are the
        shape copied here rather than a new one. Found by the census in
        `tooling/scripts/lib/detail-panels.mjs`: this panel had never been photographed, so
        nothing compared it against the design it is built from. Which docking is right at all is
        Q-297.
      */}
      <section className="page records satisfaction">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Satisfaction"
          /*
           * SUP-01, not the design's `SUP-05`. That service is the support escalation service — it
           * holds policies, timers and escalations, and the page's own recorded reason said so
           * correctly. `tests/service-codes.test.ts` holds every code named on this console to being a
           * service the page reads from, so the correction could not be left in prose.
           *
           * IAM-02 is named beside it because the first column is its field, which is the same
           * two-code chip the case timeline carries.
           */
          source="SUP-01 · IAM-02"
          description="How customers rated their support, and cases reopened."
          action={
            <button
              type="button"
              className="records__primary"
              onClick={() => {
                exportSheet(sheetFor(view.all), "csv", "satisfaction");
              }}
              title={EXPORT_RANGE_REFUSAL}
            >
              Export ratings
            </button>
          }
        />

        <div className="records__panel">
          <GridToolbar
            idPrefix="csat"
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setPage(1);
            }}
            state={undefined}
            onState={() => {
              /*
               * No state filter, and the control is drawn empty rather than removed.
               *
               * Every row carries the same state — `rated` — because the design's `top` and `coaching`
               * are a judgement against a threshold nothing publishes. A filter over one value is a
               * control that cannot change the board, so it offers nothing and says so in the toolbar
               * rather than disappearing from a design that draws it.
               */
            }}
            states={[]}
            order={order}
            onOrder={setOrder}
            onExport={(format: ExportFormat) => {
              exportSheet(sheetFor(view.all), format, "satisfaction");
            }}
          />

          <StateView
            state={viewState}
            purpose="An agent appears here as soon as one of their closed cases is rated."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "records__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="records__rows">
              {view.rows.map((row) => {
                const shown = stateOf(row);
                const agent = agentCell(row);
                return (
                  <div
                    key={row.agentSubject}
                    data-selected={row.agentSubject === selectedSubject ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={row.agentSubject === selectedSubject}
                    aria-label={`Open satisfaction for ${agent.text}`}
                    onClick={() => setSelectedSubject(row.agentSubject)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedSubject(row.agentSubject);
                    }}
                    className="records__row"
                  >
                    {/*
                     * A name where IAM-02 has one, the subject in mono where it does not — never a
                     * name derived from a subject, which would be this console inventing who somebody
                     * is.
                     */}
                    <span
                      className={agent.mono ? "records__lead records__mono" : "records__lead"}
                      {...(agent.title === undefined ? {} : { title: agent.title })}
                    >
                      {agent.text}
                    </span>
                    <span
                      className="records__cell"
                      title="SUP-01's ratedCases — how many of this agent's closed cases were rated. Not how many they handled: nothing counts the cases a customer chose not to rate."
                    >
                      {String(row.ratedCases)}
                    </span>
                    <span
                      className="records__cell"
                      title={`${exactScore(row)}, rounded to one decimal place. SUP-01 publishes the two integers rather than a mean, so this figure is reproducible.`}
                    >
                      {scoreCell(row)}
                    </span>
                    <span className="records__state">
                      <StateBadge
                        code="SUP-01"
                        status="rated"
                        classKey={shown.classKey}
                        label={shown.word}
                        title={shown.title}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? <NoMatch onClear={clearFilters} /> : null}
            <GridPager
              idPrefix="csat"
              page={page}
              pageCount={view.pageCount}
              perPage={perPage}
              onPerPage={(next) => {
                setPerPage(next);
                setPage(1);
              }}
              total={view.total}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={onNext}
              more={loaded?.cursor !== undefined}
              fetching={fetching}
            />
          </StateView>

          {note === undefined ? null : (
            <p className="records__bounded" role="status">
              {note}
            </p>
          )}

          {loaded?.membersUnread === undefined ? null : (
            <p className="records__bounded" role="status">
              {loaded.membersUnread}
            </p>
          )}

          <p className="records__bounded">{LISTING_NOTE}</p>
        </div>

        {/* The design's closing note, word for word, in its own `info` kind. */}
        <div className="records__note records__note--info">
          <span aria-hidden="true" className="records__note-glyph">
            info
          </span>
          <span>{BOARD_NOTE}</span>
        </div>
      </section>

      {selected === undefined ? null : (
        <RecordDetail
          record={agentDetail(selected)}
          open
          onClose={() => setSelectedSubject(undefined)}
        />
      )}
    </div>
  );
}

/**
 * The rectangle an export writes: the columns on screen, plus the three a file is read without.
 *
 * The subject goes in even where a name was shown, because a file read months later is matched on an
 * identifier and not on a name somebody has since changed. `Score total` goes in because the `CSAT`
 * column is rounded and a spreadsheet is where somebody recomputes it. `Latest rating` goes in
 * because a row with no date in a file about a period is a row nobody can place.
 *
 * What is **not** in it is a date range. The design's export form asks for From and To, and ADR
 * 0090's listing takes a page and a cursor and no window — so the file is the board, and
 * `EXPORT_RANGE_REFUSAL` is on the button that produces it.
 */
function sheetFor(rows: readonly AgentRow[]): {
  header: readonly string[];
  rows: readonly (readonly string[])[];
} {
  return {
    header: ["Agent", "Platform subject", "Cases", "CSAT", "Score total", "Latest rating"],
    rows: rows.map((row) => [
      row.displayName ?? "",
      row.agentSubject,
      String(row.ratedCases),
      scoreCell(row),
      String(row.scoreTotal),
      row.latestRatingAt,
    ]),
  };
}
